import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Mail,
  Send,
  FileText,
  ClipboardList,
  Shield,
  UserCheck,
  Bell,
  Clock,
  CheckCircle2,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const templateIcons = {
  initial_enquiry: UserCheck,
  documents_required: ClipboardList,
  general_requirements: FileText,
  application_update: Bell,
  protection_review: Shield
};

const templateColors = {
  initial_enquiry: "text-blue-400 bg-blue-500/20",
  documents_required: "text-amber-400 bg-amber-500/20",
  general_requirements: "text-cyan-400 bg-cyan-500/20",
  application_update: "text-purple-400 bg-purple-500/20",
  protection_review: "text-rose-400 bg-rose-500/20"
};

export default function EmailTemplates() {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    recipient_email: "",
    client_name: "",
    custom_message: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, logsRes] = await Promise.all([
        axios.get(`${API}/email/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/email/logs`, { headers: getAuthHeaders() })
      ]);
      setTemplates(templatesRes.data);
      setEmailLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const openSendDialog = (template) => {
    setSelectedTemplate(template);
    setFormData({ recipient_email: "", client_name: "", custom_message: "" });
    setSendDialogOpen(true);
  };

  const handleSend = async () => {
    if (!formData.recipient_email || !formData.client_name) {
      toast.error("Please fill in recipient email and client name");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/email/template/send`, {
        template_id: selectedTemplate.id,
        recipient_email: formData.recipient_email,
        client_name: formData.client_name,
        custom_message: formData.custom_message
      }, { headers: getAuthHeaders() });
      toast.success(`"${selectedTemplate.name}" sent successfully!`);
      setSendDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to send email");
    }
    setSending(false);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="email-templates-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">Email Templates</h1>
          <p className="text-slate-400">Send professional pre-built emails to clients for enquiries, document requests, and updates.</p>
        </div>

        {/* Templates Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {templates.map((template) => {
            const IconComponent = templateIcons[template.id] || Mail;
            const colorClass = templateColors[template.id] || "text-blue-400 bg-blue-500/20";
            return (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-[#0f172a] border-slate-800 hover:border-slate-600 transition-all h-full flex flex-col">
                  <CardContent className="p-5 flex flex-col flex-grow">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${colorClass.split(" ")[1]}`}>
                      <IconComponent className={`w-5 h-5 ${colorClass.split(" ")[0]}`} />
                    </div>
                    <h3 className="font-semibold text-white mb-1">{template.name}</h3>
                    <p className="text-xs text-slate-500 mb-4 flex-grow">{template.subject}</p>
                    <Button
                      onClick={() => openSendDialog(template)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid={`send-template-${template.id}`}
                    >
                      <Send className="w-4 h-4 mr-2" /> Send Email
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Recent Sent Emails */}
        <Card className="bg-[#0f172a] border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              Recently Sent Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailLogs.length === 0 ? (
              <div className="py-8 text-center">
                <Mail className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No emails sent yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {emailLogs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-3 rounded-lg bg-[#020617] border border-slate-800">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{log.template_name}</p>
                        <p className="text-xs text-slate-500">To: {log.recipient_email} ({log.client_name})</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Dialog */}
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent className="bg-[#0f172a] border-slate-700">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Send: {selectedTemplate?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Recipient Email *</Label>
                <Input
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                  placeholder="client@example.com"
                  className="bg-[#020617] border-slate-700"
                  data-testid="template-recipient-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-[#020617] border-slate-700"
                  data-testid="template-client-name"
                />
              </div>
              {selectedTemplate?.id === "application_update" && (
                <div className="space-y-2">
                  <Label>Status Message</Label>
                  <Textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
                    placeholder="e.g., Your application has been submitted to the lender..."
                    className="bg-[#020617] border-slate-700"
                    rows={3}
                    data-testid="template-custom-message"
                  />
                </div>
              )}
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">Subject:</p>
                <p className="text-sm text-slate-300">{selectedTemplate?.subject}</p>
              </div>
              <Button
                onClick={handleSend}
                disabled={sending}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-send-template"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Email</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
